import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { type StepUpState, type UserEntity, UserType } from "@/shared/types/user.entity";

import { verificationEmailChanged } from "./registerSlice";

export interface CustomerState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading?: boolean;
  requires2FA?: boolean;
  mustChangePassword?: boolean;
  user?: UserEntity | null;
  error?: string | null;
}

const initialState: CustomerState = {
  isAuthenticated: false,
  isInitialized: false,
  isLoading: true,
  requires2FA: false,
  mustChangePassword: false,
  user: null,
  error: null,
};

export const authSlide = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // isInitialized survives resets: session hydration happens once per page load.
    resetState: (state) => ({ ...initialState, isInitialized: state.isInitialized }),
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
    },
    setLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    },
    setUser: (state, action: PayloadAction<{ user?: UserEntity | null }>) => {
      state.isAuthenticated = !!action.payload.user;
      state.user = action.payload.user;
    },
    setRequires2FA: (state, action: PayloadAction<{ requires2FA?: boolean }>) => {
      state.requires2FA = action.payload.requires2FA;
    },
    setMustChangePassword: (state, action: PayloadAction<{ mustChangePassword?: boolean }>) => {
      state.mustChangePassword = action.payload.mustChangePassword;
    },
    setError: (state, action: PayloadAction<{ error?: string | null }>) => {
      state.error = action.payload.error;
    },
    clearError: (state) => {
      state.error = null;
    },
    update2FAStatus: (state, action: PayloadAction<{ enabled: boolean }>) => {
      if (state.user) {
        state.user.two_factor_enabled = action.payload.enabled;
        // The server keeps one step-up method: enabling the authenticator removes the security PIN, and the
        // PIN was already gone when the authenticator is disabled — so neither direction leaves a PIN.
        state.user.step_up_method = action.payload.enabled ? "totp" : null;
        state.user.security_pin_enabled = false;
        state.user.step_up_locked_until = null;
        state.user.security_pin_reset_required = false;
        // The email backup is opt-in for the authenticator: switching to it turns the backup off, and with no
        // method left there is nothing for it to back up. Members can turn it back on in Settings.
        state.user.step_up_email_enabled = false;
        state.user.step_up_email_available =
          action.payload.enabled && state.user.user_type === UserType.ORGANIZATION_MEMBER;
      }
    },
    /** The step-up state the PIN endpoints and `/auth/me` answer with, merged without touching the rest. */
    updateStepUpState: (state, action: PayloadAction<Partial<StepUpState>>) => {
      if (state.user) {
        Object.assign(state.user, action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    // The address was corrected from the verify screen: patch it in place. Re-reading `/auth/me` would cost a
    // round trip, and until it landed the screen would still show — and resend to — the old address.
    builder.addCase(verificationEmailChanged, (state, action) => {
      if (!state.user) return;
      state.user.email = action.payload.email;
      if (action.payload.organizationEmailUpdated && state.user.organization) {
        state.user.organization.email = action.payload.email;
      }
    });
  },
});

export const {
  resetState,
  logout,
  setUser,
  setLoading,
  setInitialized,
  setRequires2FA,
  setMustChangePassword,
  setError,
  clearError,
  update2FAStatus,
  updateStepUpState,
} = authSlide.actions;
